import React, {ReactNode} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as nyc from 'ui-library/NYC';

export interface SidepanelSkeletonProps {
  isExpanded: boolean;
  header: ReactNode;
  content: ReactNode;
}

/*
 * Skeleton that can render either an expanded or collapsed sidepanel with header and content
 */
export default function SidepanelSkeleton({
  isExpanded,
  header,
  content,
}: SidepanelSkeletonProps): JSX.Element {
  return isExpanded ? (
    <ExpandedSidepanelSkeleton header={header} content={content} />
  ) : (
    <CollapsedSidepanelSkeleton header={header} content={content} />
  );
}

/*
 * Skeleton that renders expanded sidepanel header and content (for example, an expanded table of listed
 * items) in the nyc layout
 */
export function ExpandedSidepanelSkeleton({
  header,
  content,
  dataTestAttrs,
}: {
  header: ReactNode;
  content: ReactNode;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  return (
    <nyc.Container
      className="sidepanel expandedContainer"
      size="lg"
      dataTestAttrs={dataTestAttrs ?? {component: 'ExpandedSidepanel', element: 'root', key: 'expanded'}}
    >
      <nyc.Row>
        <nyc.Col lg={1} />
        <nyc.Col lg={20}>{header}</nyc.Col>
        <nyc.Col lg={1} />
      </nyc.Row>
      <nyc.Row fluid={true}>
        <nyc.Col lg={1} />
        <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
          {content}
        </nyc.Col>
        <nyc.Col lg={1} />
      </nyc.Row>
    </nyc.Container>
  );
}

/*
 * Skeleton that renders collapsed sidepanel header and content in the nyc layout
 */
export function CollapsedSidepanelSkeleton({
  header,
  content,
  dataTestAttrs,
}: {
  header: ReactNode;
  content: ReactNode;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  return (
    <nyc.Container
      className="sidepanel"
      size="sidepanel"
      dataTestAttrs={dataTestAttrs ?? {component: 'CollapsedSidepanel', element: 'root', key: 'collapsed'}}
    >
      <nyc.Row>{header}</nyc.Row>
      <nyc.Row fluid={true} className="u-vertical-flex">
        <div className="u-flex-grow u-position-relative">
          <div className="u-absolute-scroll-container">{content}</div>
        </div>
      </nyc.Row>
    </nyc.Container>
  );
}
